import Modal from "@via/components/modal";
import { ModalSize } from "@via/utils/constants";
import { FieldFormData } from "@via/pages/data/premium-dataset/utils/helper";
import { type FC, useState, useCallback, useRef } from "react";
import AdditionalFieldForm from "./additional-field-form";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

interface AdditionalFieldsModalProps {
  isOpen: boolean;
  onClose: () => void;
  additionalColumns: string[];
  onFieldsCreated: (
    createdFields: FieldFormData[],
    fieldDataMap: Record<string, FieldFormData>
  ) => void;
  onSkip: () => void;
}

const AdditionalFieldsModal: FC<AdditionalFieldsModalProps> = ({
  isOpen,
  onClose,
  additionalColumns,
  onFieldsCreated,
  onSkip,
}) => {
  const [fieldDataMap, setFieldDataMap] = useState<
    Record<string, FieldFormData>
  >({});
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(additionalColumns)
  );
  const [isCreating, setIsCreating] = useState(false);
  const fieldDataMapRef = useRef<Record<string, FieldFormData>>({});

  // Handle field data changes from individual forms
  const handleFieldDataChange = useCallback(
    (columnName: string, data: FieldFormData) => {
      fieldDataMapRef.current = {
        ...fieldDataMapRef.current,
        [columnName]: data, // Use original column name as key
      };
      setFieldDataMap(fieldDataMapRef.current);
    },
    []
  );

  // Handle field selection toggle
  const handleFieldSelection = useCallback(
    (columnName: string, isSelected: boolean) => {
      setSelectedFields(prev => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(columnName);
        } else {
          newSet.delete(columnName);
        }
        return newSet;
      });
    },
    []
  );

  const handleCreateFields = async () => {
    setIsCreating(true);
    try {
      // Only create fields that are selected
      const fieldsToCreate = Array.from(selectedFields)
        .map(columnName => {
          // Find the field data using the original column name as key
          return fieldDataMap[columnName];
        })
        .filter(
          (fieldData): fieldData is FieldFormData =>
            fieldData !== undefined &&
            Boolean(fieldData.name) &&
            Boolean(fieldData.label) &&
            Boolean(fieldData.type)
        );

      await onFieldsCreated(fieldsToCreate, fieldDataMap);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle skipping field creation
  const handleSkip = () => {
    onSkip();
  };

  // Check if selected fields have valid data
  const selectedFieldsValid = Array.from(selectedFields).every(columnName => {
    const fieldData = fieldDataMap[columnName];
    return fieldData && fieldData.name && fieldData.label && fieldData.type;
  });

  const hasSelectedFields = selectedFields.size > 0;

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Create Fields for Additional Columns"
      size={ModalSize.Large}
      showButtons={false}
      disableClose={isCreating}>
      <div className="flex h-[70vh] flex-col">
        {/* Header Information - Fixed */}
        <div className="flex-shrink-0 rounded-lg bg-blue-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-blue-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Additional Columns Found
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  We found {additionalColumns.length}{" "}
                  {additionalColumns.length === 1 ? "column" : "columns"} in
                  your file that{" "}
                  {additionalColumns.length === 1 ? "isn't" : "aren't"} in the
                  selected sample tracker. Select the fields to create, or skip
                  to leave them out.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Field Forms Header - Fixed */}
        <div className="flex-shrink-0 border-b border-gray-200 pb-4 pt-6">
          <h4 className="text-lg font-medium text-gray-900">
            Configure Field Properties
          </h4>
        </div>

        {/* Scrollable Field Forms */}
        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-4 pr-2">
            {additionalColumns.map(columnName => (
              <AdditionalFieldForm
                key={columnName}
                columnName={columnName}
                onFieldDataChange={data =>
                  handleFieldDataChange(columnName, data)
                }
                initialData={fieldDataMap[columnName]}
                isSelected={selectedFields.has(columnName)}
                onSelectionChange={isSelected =>
                  handleFieldSelection(columnName, isSelected)
                }
              />
            ))}
          </div>
        </div>

        {/* Action Buttons - Fixed */}
        <div className="flex-shrink-0 flex justify-end space-x-3 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleSkip}
            disabled={isCreating}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
            Skip & Continue
          </button>

          <button
            type="button"
            onClick={handleCreateFields}
            disabled={!hasSelectedFields || !selectedFieldsValid || isCreating}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
            {isCreating ? (
              <>
                <svg
                  className="mr-2 h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Fields...
              </>
            ) : (
              `Create ${selectedFields.size} Selected Field${selectedFields.size !== 1 ? "s" : ""}`
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdditionalFieldsModal;
